import React, { useEffect, useState } from 'react';
import { bottomDrawerHelperActions } from 'src/components/20-helper-views/bottom-drawer/useBottomDrawer';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import { getConflictedNoteTabIds, useUpdateNoteMutation, } from 'src/modules/exam-preparation/graphql/notes/update';
import NoteFormDrawer from '../Notes/NoteFormDrawer';
import useNoteEditorDraft from './hooks/useNoteEditorDraft';
import useNoteTabActions from './hooks/useNoteTabActions';
export default function useData(note, draftOptions) {
    const [activeTabID, setActiveTabID] = useState(note.tabs?.[0]?.id ?? '');
    const [draftState, draftActions, draftHelpers] = useNoteEditorDraft(note, draftOptions);
    const [updateNoteMutationState, updateNoteMutationActions] = useUpdateNoteMutation({
        onCompleted() {
            draftActions.reset();
        },
    });
    const [, noteTabActions] = useNoteTabActions(note, draftActions);
    // If a new tab is added, set the active tab to the newly added tab
    useEffect(() => {
        const seq = draftState.noteTabsSequence;
        if (seq.length > 0 && !seq.includes(activeTabID)) {
            setActiveTabID(seq[0]);
        }
    }, [draftState.noteTabsSequence, activeTabID]);
    const setSelectedTab = useStableCallback((tabID) => {
        setActiveTabID(tabID);
    });
    const addTab = useStableCallback(() => {
        noteTabActions.addTab((newDisplayId) => {
            setActiveTabID(newDisplayId);
        });
    });
    const saveNote = useStableCallback(async () => {
        if (!draftHelpers.hasUnsavedChanges()) {
            return { conflictedNoteTabIDs: [] };
        }
        const variables = draftHelpers.getChangesPayload();
        if (variables.data.data === undefined && variables.data.tabs === undefined) {
            return { conflictedNoteTabIDs: [] };
        }
        const mutationResult = await updateNoteMutationActions.update({ variables });
        const updated = mutationResult?.data?.ep_updateNote;
        if (!updated) {
            return { conflictedNoteTabIDs: [] };
        }
        if (updated.sentForReview) {
            return { conflictedNoteTabIDs: [], submittedForReview: true };
        }
        const conflictedNoteTabIDs = getConflictedNoteTabIds(updated);
        return { conflictedNoteTabIDs };
    });
    const editNote = useStableCallback(() => {
        if (!note) {
            return;
        }
        const mergedNote = {
            ...note,
            data: {
                ...note.data,
                title: draftState.note.data.title,
                topics: draftState.note.data.topics,
            },
        };
        bottomDrawerHelperActions.show({
            component: (React.createElement(NoteFormDrawer, { note: mergedNote, draftApply: (data) => {
                    draftActions.updateNote(data);
                    bottomDrawerHelperActions.hide();
                } })),
        });
    });
    const state = {
        activeTabID,
        draft: draftState,
        updateNoteMutation: updateNoteMutationState,
    };
    const actions = {
        setSelectedTab,
        saveNote,
        editNote,
        draft: draftActions,
        noteTab: {
            ...noteTabActions,
            addTab,
        },
    };
    const helpers = {
        draft: draftHelpers,
    };
    return [state, actions, helpers];
}
